import { useMemo, useState } from 'react';
import { useGetAllContactEmployees } from '@/api/business-partner/business-partner.hooks';
import type { ContactEmployee } from '@/api/contact-employee/contact-employee.types';
import { DsInput } from '@/modules/internal-v2/ds';

/**
 * Reusable typeahead over the caller's accessible contact employees.
 * Backed by `/v1/contact-employees`, which scopes the result set server-side.
 *
 * Two modes:
 *
 * - `mode: 'single'` — renders a clearable selected pill once a contact is
 *   picked, the input otherwise. Used by the email-builder preview-recipient
 *   picker, where one contact drives variable resolution for the preview.
 * - `mode: 'multi'` — never renders selected state itself; just calls
 *   `onAdd(id)` on each pick and removes already-picked contacts from the
 *   suggestion list. The caller renders the selected set however it likes
 *   (e.g. as recipient rows in the email-builder recipients tab).
 *
 * The single/multi split is deliberate: selected-state UX differs sharply
 * between the two callers, and a one-size component would couple every
 * future use site to whichever layout the first one chose.
 */
export interface ContactEmployeeOption {
  id: number;
  name: string;
  email: string | null;
  businessPartnerName?: string;
}

interface CommonProps {
  /** Inline placeholder shown in the search input. */
  placeholder?: string;
  /** Restrict suggestions to contacts whose BP has one of these SAP types. */
  businessPartnerTypeFilter?: ('cCustomer' | 'cSupplier')[];
  /** Optional extra predicate (e.g. require non-null email). */
  filter?: (contact: ContactEmployee) => boolean;
  /** Show at most this many matches (default 20). */
  maxResults?: number;
  className?: string;
}

interface SingleProps extends CommonProps {
  mode: 'single';
  /** Currently picked contact-employee id; `null` when no recipient is selected. */
  value: number | null;
  onChange: (next: number | null) => void;
  /** Label rendered before the input — small uppercase eyebrow. */
  label?: string;
}

interface MultiProps extends CommonProps {
  mode: 'multi';
  /** Currently selected ids — excluded from the suggestion list. */
  selectedIds: number[];
  onAdd: (id: number) => void;
}

export type ContactEmployeePickerProps = SingleProps | MultiProps;

export function ContactEmployeePicker(props: ContactEmployeePickerProps) {
  const { data: contacts = [], isLoading } = useGetAllContactEmployees();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const max = props.maxResults ?? 20;
  const typeFilter = props.businessPartnerTypeFilter;
  const extraFilter = props.filter;

  const selectedId = props.mode === 'single' ? props.value : null;
  const selectedSet = useMemo(
    () =>
      props.mode === 'multi' ? new Set<number>(props.selectedIds) : new Set<number>(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.mode === 'multi' ? props.selectedIds : null],
  );

  const selectedContact = useMemo(
    () =>
      selectedId != null ? contacts.find((c) => c.id === selectedId) : undefined,
    [contacts, selectedId],
  );

  // Trim + lowercase once per change; match on contact name, BP name, email.
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = contacts.filter((c) => {
      if (selectedSet.has(c.id)) return false;
      if (typeFilter && !typeFilter.includes(c.businessPartner?.type as never))
        return false;
      if (extraFilter && !extraFilter(c)) return false;
      return true;
    });
    if (q.length === 0) return filtered.slice(0, max);
    return filtered
      .filter((c) => {
        const hay =
          `${c.name} ${c.businessPartner?.name ?? ''} ${c.email ?? ''}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, max);
  }, [contacts, query, selectedSet, typeFilter, extraFilter, max]);

  const handlePick = (id: number) => {
    if (props.mode === 'single') props.onChange(id);
    else props.onAdd(id);
    setOpen(false);
    setQuery('');
  };

  const handleClear = () => {
    if (props.mode === 'single') props.onChange(null);
    setQuery('');
  };

  // Single-select with an active pick: render the pill and exit early — no
  // input shown until the user clears it.
  if (props.mode === 'single' && selectedContact) {
    return (
      <div className={['eb-preview-recipient', props.className].filter(Boolean).join(' ')}>
        {props.label && (
          <span className="eb-preview-recipient-label">{props.label}</span>
        )}
        <span className="eb-preview-recipient-selected">
          <span className="eb-preview-recipient-name">{selectedContact.name}</span>
          {selectedContact.businessPartner?.name && (
            <span className="eb-preview-recipient-bp">
              · {selectedContact.businessPartner.name}
            </span>
          )}
          <button
            type="button"
            className="eb-preview-recipient-clear"
            aria-label="Empfänger zurücksetzen"
            onClick={handleClear}
          >
            ×
          </button>
        </span>
      </div>
    );
  }

  const placeholder =
    props.placeholder ??
    (isLoading
      ? 'Lade Kontakte…'
      : props.mode === 'multi'
        ? 'Kontakt hinzufügen…'
        : 'Empfänger suchen oder leer lassen');

  return (
    <div className={['eb-preview-recipient', props.className].filter(Boolean).join(' ')}>
      {props.mode === 'single' && props.label && (
        <span className="eb-preview-recipient-label">{props.label}</span>
      )}

      <span className="eb-preview-recipient-search">
        <DsInput
          value={query}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            // Delay so a click on a menu item still registers.
            window.setTimeout(() => setOpen(false), 120);
          }}
        />
        {open && matches.length > 0 && (
          <ul className="eb-preview-recipient-menu">
            {matches.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className="eb-preview-recipient-option"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handlePick(c.id)}
                >
                  <span className="eb-preview-recipient-option-name">{c.name}</span>
                  {c.businessPartner?.name && (
                    <span className="eb-preview-recipient-option-bp">
                      {c.businessPartner.name}
                      {c.email && ` · ${c.email}`}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </span>
    </div>
  );
}
